// src/hooks/useShopifyEmbeddedRedirect.js
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook that handles smart redirects for Shopify embedded apps
 * Prevents unnecessary OAuth flows for returning users
 */
export function useShopifyEmbeddedRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get('shop');
    const host = urlParams.get('host');
    const isEmbedded = host !== null;
    
    // Only redirect if we're in embedded context and on certain pages
    if (isEmbedded && shop) {
      const currentPath = location.pathname;
      
      // Check if we have a stored successful setup for this shop
      const setupComplete = sessionStorage.getItem(`shop_${shop}_setup_complete`);
      
      if (setupComplete === 'true') {
        // Skip OAuth pages and go directly to dashboard
        const oauthPages = [
          '/link-shopify-account',
          '/connect-shopify',
          '/ConnectSuccessPage',
          '/integrations'
        ];
        
        if (oauthPages.includes(currentPath)) {
          console.log(`[Shopify Redirect] Shop ${shop} already configured, redirecting to dashboard`);
          navigate(`/dashboard?shop=${shop}&host=${host}`);
        }
      }
    }
  }, [location, navigate]);
}

// Mark a shop as fully set up
export function markShopSetupComplete(shop) {
  sessionStorage.setItem(`shop_${shop}_setup_complete`, 'true');
  
  // Also store timestamp to expire after 24 hours
  const timestamp = Date.now();
  sessionStorage.setItem(`shop_${shop}_setup_timestamp`, timestamp.toString());
}

// Check if setup is still valid (within 24 hours)
export function isShopSetupValid(shop) {
  const setupComplete = sessionStorage.getItem(`shop_${shop}_setup_complete`);
  const timestamp = sessionStorage.getItem(`shop_${shop}_setup_timestamp`);
  
  if (setupComplete !== 'true' || !timestamp) {
    return false;
  }
  
  // Check if less than 24 hours old
  const age = Date.now() - parseInt(timestamp);
  const twentyFourHours = 24 * 60 * 60 * 1000;
  
  if (age > twentyFourHours) {
    // Expired, clear the flags
    sessionStorage.removeItem(`shop_${shop}_setup_complete`);
    sessionStorage.removeItem(`shop_${shop}_setup_timestamp`);
    return false;
  }
  
  return true;
}